import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ChevronDown, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

/**
 * Searchable combobox to pick a member by name / passport number, used as a
 * fallback when Web NFC is unavailable (iOS Safari/Chrome) or when an operator
 * wants to mark a member without scanning. Selecting a member returns its
 * `nfc_token` (via `onSelect`) so the parent can navigate to /check-in/{token}.
 */
export default function MemberNfcPicker({ onSelect, testId = "member-nfc-picker", includeChildren = false }) {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Lazy-load members when first opened.
  useEffect(() => {
    if (!open || members.length > 0 || loading) return;
    setLoading(true);
    setError(null);
    api
      .get("/members", { params: includeChildren ? { include_children: true } : {} })
      .then(({ data }) => {
        const filtered = (data || []).filter((m) => m.nfc_token);
        setMembers(filtered);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || (t("memberNfcPicker.failed_to_load_members")));
      })
      .finally(() => setLoading(false));
  }, [open, members.length, loading]);

  // Auto-focus search input when popover opens.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members.slice(0, 60);
    const out = [];
    for (const m of members) {
      const fn = (m.first_name || "").toLowerCase();
      const ln = (m.last_name || "").toLowerCase();
      const passport = (m.passport_number || "").toLowerCase();
      if (fn.includes(q) || ln.includes(q) || passport.includes(q) || `${fn} ${ln}`.includes(q) || `${ln} ${fn}`.includes(q)) {
        out.push(m);
        if (out.length >= 60) break;
      }
    }
    return out;
  }, [members, query]);

  const select = (m) => {
    setOpen(false);
    setQuery("");
    onSelect(m);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }} data-testid={`${testId}-wrapper`}>
      <button
        type="button"
        className="btn-outline-gold"
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        style={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          minHeight: 44,
          padding: "0.6rem 0.9rem",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <User size={14} />
          <span>{open ? (t("memberNfcPicker.search_member")) : t("attendance.manualPickButton")}</span>
        </span>
        <ChevronDown size={14} color="#9a7530" />
      </button>

      {open && (
        <div
          data-testid={`${testId}-popover`}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            background: "#fff",
            border: "1px solid #c9a84c",
            borderRadius: 4,
            boxShadow: "0 10px 30px -8px rgba(13,27,42,0.25)",
            zIndex: 50,
            maxHeight: 360,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "0.5rem 0.75rem",
              borderBottom: "1px solid #ead8a8",
              background: "#fdf4e0",
            }}
          >
            <Search size={14} color="#9a7530" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("memberNfcPicker.last_name_first_name_or")}
              data-testid={`${testId}-search`}
              autoComplete="off"
              style={{
                border: 0,
                outline: 0,
                flex: 1,
                fontFamily: "Crimson Text, serif",
                fontSize: "0.95rem",
                background: "transparent",
                color: "#0d1b2a",
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                title="Clear"
                style={{ background: "transparent", border: 0, cursor: "pointer" }}
                data-testid={`${testId}-clear`}
              >
                <X size={14} color="#9a7530" />
              </button>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && (
              <div
                style={{ padding: "1rem", textAlign: "center", color: "#9a7530", fontStyle: "italic", fontSize: "0.85rem" }}
                data-testid={`${testId}-loading`}
              >
                {t("memberNfcPicker.loading")}
              </div>
            )}
            {error && !loading && (
              <div
                style={{ padding: "1rem", textAlign: "center", color: "#7c2a2a", fontSize: "0.85rem" }}
                data-testid={`${testId}-error`}
              >
                {error}
              </div>
            )}
            {!loading && !error && filtered.length === 0 && (
              <div
                style={{ padding: "1rem", textAlign: "center", color: "#9a7530", fontStyle: "italic", fontSize: "0.85rem" }}
                data-testid={`${testId}-empty`}
              >
                {query
                  ? (t("memberNfcPicker.no_matches"))
                  : (t("memberNfcPicker.no_members_available"))}
              </div>
            )}
            {!loading && !error && filtered.map((m) => (
              <button
                type="button"
                key={m.id}
                onClick={() => select(m)}
                data-testid={`${testId}-option-${m.id}`}
                style={{
                  display: "flex",
                  width: "100%",
                  textAlign: "left",
                  padding: "0.6rem 0.85rem",
                  background: "transparent",
                  border: 0,
                  color: "#0d1b2a",
                  cursor: "pointer",
                  borderBottom: "1px solid #f6efde",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: "#fdf4e0",
                    border: "1px solid #c9a84c",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#9a7530",
                    flexShrink: 0,
                  }}
                >
                  <User size={14} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: "Cinzel, serif",
                      letterSpacing: "0.06em",
                      fontSize: "0.92rem",
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {m.last_name} {m.first_name}
                  </span>
                  {(m.passport_number || m.grade) && (
                    <span style={{ fontSize: "0.72rem", color: "#9a7530", letterSpacing: "0.04em" }}>
                      {m.passport_number || ""}
                      {m.passport_number && m.grade ? " · " : ""}
                      {m.grade || ""}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
          {!loading && !error && members.length > filtered.length && (
            <div
              style={{
                padding: "0.4rem 0.85rem",
                background: "#f6efde",
                fontSize: "0.7rem",
                color: "#9a7530",
                textAlign: "center",
                borderTop: "1px solid #ead8a8",
              }}
              data-testid={`${testId}-overflow-note`}
            >
              {query
                ? t("picker.showingFiltered", { count: filtered.length })
                : t("picker.showingOf", { count: filtered.length, total: members.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
